import React, { useState, useRef } from 'react';
import { ThemeProvider, createTheme, CssBaseline, CircularProgress, Box } from '@mui/material';
import { useQuery } from 'react-query';

import { api } from './services/api';
import AppLayout from './components/layout/AppLayout';
import FileUploadSection, { FileUploadSectionRef } from './components/FileUploadSection';
import QuerySection from './components/QuerySection';
import ResultsSection from './components/ResultsSection';
import ConfigDrawer from './components/config/ConfigDrawer';
import LoginPage from './components/auth/LoginPage';
import { QueryResult } from './types/api';
import { AlertMessage } from './components/common/AlertMessage';
import { AuthProvider, useAuth } from './context/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#2074d4',
    },
    secondary: {
      main: '#558b2f',
    },
    background: {
      default: '#f5f7fa',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.2rem',
      fontWeight: 500,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 500,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// Main application component (requires authentication)
const MainApp: React.FC = () => {
  // Ref to the file upload section for resetting data
  const fileUploadRef = useRef<FileUploadSectionRef>(null);
  // State for config drawer
  const [configOpen, setConfigOpen] = useState(false);
  
  // State for file upload
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false);
  
  // State for query and results
  const [activeQueryType, setActiveQueryType] = useState<'nl' | 'sql'>('nl');
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryResults, setQueryResults] = useState<QueryResult | null>(null);
  
  // State for alerts
  const [alert, setAlert] = useState<{
    message: string;
    type: 'error' | 'success' | 'info' | 'warning';
    open: boolean;
  }>({
    message: '',
    type: 'info',
    open: false,
  });

  // Check health of API on load
  const { data: healthData, isError: healthError } = useQuery(
    'health',
    api.health.check,
    {
      onError: (error) => {
        setAlert({
          message: `API Connection Failed: ${error}`,
          type: 'error',
          open: true,
        });
      },
      onSuccess: (data) => {
        if (data.database === 'connected') {
          setFileUploadSuccess(true);
        }
      },
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  // Check data summary on load
  const { data: summaryData } = useQuery(
    'dataSummary',
    api.files.getDataSummary,
    {
      enabled: healthData?.status === 'ok',
      onSuccess: (data) => {
        setFileUploadSuccess(data.has_data);
      },
      onError: () => {
        setFileUploadSuccess(false);
      },
    }
  );

  // Function to handle file upload success
  const handleFileUploadSuccess = () => {
    setFileUploadSuccess(true);
    setAlert({
      message: 'Files uploaded successfully!',
      type: 'success',
      open: true,
    });
  };

  // Function to handle query execution
  const executeQuery = async () => {
    if (!currentQuery.trim()) {
      setAlert({
        message: 'Please enter a query',
        type: 'warning',
        open: true,
      });
      return;
    }

    try {
      let result;
      if (activeQueryType === 'nl') {
        result = await api.queries.executeNl(currentQuery);
      } else {
        result = await api.queries.executeSql(currentQuery);
      }
      setQueryResults(result);
    } catch (error) {
      setAlert({
        message: `Query execution failed: ${error}`,
        type: 'error',
        open: true,
      });
    }
  };

  // Function to handle closing the alert
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };
  
  // Function to handle going back to file upload view
  const handleBackToStart = () => {
    // Reset query and results state
    setCurrentQuery('');
    setQueryResults(null);
    setFileUploadSuccess(false);
    // Reset the file upload component via ref
    if (fileUploadRef.current) {
      fileUploadRef.current.resetData();
    }
    setAlert({
      message: 'Ready to load new data',
      type: 'info',
      open: true,
    });
  };

  return (
    <AppLayout 
      onConfigOpen={() => setConfigOpen(true)} 
      hasData={fileUploadSuccess}
      dataSummary={summaryData}
      onBackToStart={handleBackToStart}
    >
      {!fileUploadSuccess ? (
        <FileUploadSection ref={fileUploadRef} onUploadSuccess={handleFileUploadSuccess} />
      ) : (
        <>
          <QuerySection
            queryType={activeQueryType}
            onQueryTypeChange={setActiveQueryType}
            query={currentQuery}
            onQueryChange={setCurrentQuery}
            onExecuteQuery={executeQuery}
            onBackToStart={handleBackToStart}
          />
          <ResultsSection results={queryResults} />
        </>
      )}
      
      <ConfigDrawer 
        open={configOpen} 
        onClose={() => setConfigOpen(false)} 
      />
      
      <AlertMessage
        open={alert.open}
        message={alert.message}
        severity={alert.type}
        onClose={handleCloseAlert}
      />
    </AppLayout>
  );
};

// App wrapper with authentication
const App: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh' 
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isAuthenticated ? <MainApp /> : <LoginPage />}
    </ThemeProvider>
  );
};

// Final export wrapped with AuthProvider
const AppWithAuth: React.FC = () => (
  <AuthProvider>
    <App />
  </AuthProvider>
);

export default AppWithAuth;
